// Oturum yönetimi — motoru uygulamaya bağlayan katman (9.34 · D-607, D-608).
//
// Saf kripto (keygen, X3DH, ratchet) burada birleşiyor: kişi başına bir ratchet
// oturumu kurulur, güvenli depoda saklanır, mesaj o oturumla şifrelenip çözülür.
// Sunucuya ve taşımaya HENÜZ dokunmuyor — bu katman yalnız "bu kişi için düz
// metni zarfa çevir / zarftan düz metni çıkar" diyor.
//
// İlk mesaj bir ÖNCÜL (prekey) zarfıdır: alıcının oturumu kurabilmesi için
// başlatanın kimlik/geçici anahtarını ve kullandığı prekey kimliklerini taşır.
// Başlatan, karşıdan ilk yanıtı çözene dek öncülü tekrarlar — ilk mesaj kaybolsa
// bile alıcı sonraki bir mesajdan oturumu kurabilsin.
import { getSecret, setSecret, deleteSecret, secureStorageAvailable } from './secureStorage';
import { api, ApiError, PrekeyBundle, MessageEnvelope } from '../services/api';
import { x25519PublicKey } from './crypto';
import { identitiesSeen } from './identityWatch';
import { initiateSession, respondSession } from './x3dh';
import {
  initInitiator,
  initResponder,
  ratchetEncrypt,
  ratchetDecrypt,
  RatchetState,
  RatchetMessage,
} from './doubleRatchet';
import {
  IDENTITY_SECRET,
  IDENTITY_PUBLIC,
  DEVICE_ID as K_DEVICE_ID,
  signedPreKeySecret,
  oneTimePreKeySecret,
  session as sessionKey,
} from './storageKeys';

type Prelude = {
  identityKey: string;
  ephemeralKey: string;
  signedPreKeyId: number;
  oneTimePreKeyId: number | null;
};

// `from` = GÖNDERENİN cihaz numarası (9.34 · D-625). Alıcı oturumu (kişi, cihaz)
// çiftine göre saklıyor; bu olmadan aynı kişinin iki cihazı tek durumu paylaşır
// ve biri ötekinin ilerlemesini siler. Eski sürümlerin zarfında yok; orada
// LEGACY_DEVICE kullanılıyor, yani eski oturumlar kendi köşesinde kalıyor.
export type SessionEnvelope =
  | { type: 'prekey'; prelude: Prelude; message: RatchetMessage; from?: number }
  | { type: 'normal'; message: RatchetMessage; from?: number };

// Cihaz numarası taşımayan zarflar için ayrılmış numara. Gerçek cihaz numaraları
// 1'den başlıyor, yani çakışmıyor.
export const LEGACY_DEVICE = 0;

const gonderenCihaz = (e: SessionEnvelope): number => e.from ?? LEGACY_DEVICE;

type StoredSession = {
  state: RatchetState;
  ad: string;
  role: 'initiator' | 'responder';
  // Başlatan karşıdan bir mesaj çözene dek false; o sürece öncül tekrarlanır.
  peerReplied: boolean;
  prelude?: Prelude;
};

// Kişi başına sıra (9.34 · D-617).
//
// Şifreleme ve çözme "yükle → ilerlet → kaydet" yapıyor ve ratchet durumu
// KİŞİ BAŞINA tek. Aynı kişi için iki iş üst üste binerse ikisi de aynı durumu
// okur, ikisi de ilerletir ve sonra yazan öbürünün ilerlemesini siler — tüketilen
// anahtar geri gelmediği için o oturum kalıcı olarak bozulur.
//
// Sohbet ekranı tek çözücüyken bu uzak bir ihtimaldi. Gelen kutusu da çözmeye
// başlayınca (liste önizlemesi) iki bağımsız çözücü oluyor ve sohbet açıkken
// listenin tazelenmesi sıradan bir olay. JS tek iş parçacıklı olduğu için
// işleri kişi başına zincirlemek yeterli.
const kuyruk = new Map<number, Promise<unknown>>();

function sirayla<T>(peerId: number, is: () => Promise<T>): Promise<T> {
  const onceki = kuyruk.get(peerId) ?? Promise.resolve();
  // Önceki iş hata verdiyse sıra durmamalı; yalnız sırası gelmiş sayılır.
  const sonuc = onceki.then(is, is);
  // Sıranın kendisi hatayla kopmasın diye yutulmuş bir kopya saklanıyor;
  // hata çağırana `sonuc` üzerinden gidiyor.
  kuyruk.set(peerId, sonuc.then(() => undefined, () => undefined));
  return sonuc;
}

async function loadSession(peerId: number, deviceId: number): Promise<StoredSession | null> {
  const raw = await getSecret(sessionKey(peerId, deviceId));
  return raw ? (JSON.parse(raw) as StoredSession) : null;
}

async function saveSession(peerId: number, deviceId: number, s: StoredSession): Promise<void> {
  await setSecret(sessionKey(peerId, deviceId), JSON.stringify(s));
}

async function loadIdentity(): Promise<{ pub: string; sec: string }> {
  const pub = await getSecret(IDENTITY_PUBLIC);
  const sec = await getSecret(IDENTITY_SECRET);
  if (!pub || !sec) throw new Error('Cihaz kimliği yok — önce registerDeviceKeys');
  return { pub, sec };
}

// Başlatan olarak oturum kur: karşının demetini al, X3DH çalıştır, ratchet'i
// başlat. Öncül, ilk mesajlarla gönderilecek.
async function establishInitiator(
  identity: { pub: string; sec: string },
  bundle: PrekeyBundle,
): Promise<StoredSession> {
  const init = initiateSession({
    myIdentityPublic: identity.pub,
    myIdentitySecret: identity.sec,
    peerBundle: {
      identityKey: bundle.identity_key,
      signedPreKey: bundle.signed_prekey,
      oneTimePreKey: bundle.one_time_prekey,
    },
  });
  return {
    state: initInitiator(init.sharedSecret, bundle.signed_prekey.key),
    ad: init.associatedData,
    role: 'initiator',
    peerReplied: false,
    prelude: {
      identityKey: identity.pub,
      ephemeralKey: init.ephemeralPublicKey,
      signedPreKeyId: init.usedSignedPreKeyId,
      oneTimePreKeyId: init.usedOneTimePreKeyId,
    },
  };
}

/** Bir kişiye gönderilecek düz metni şifreli zarfa çevir. */
export function encryptMessage(
  token: string,
  peerId: number,
  plaintext: string,
): Promise<SessionEnvelope> {
  return sirayla(peerId, async () => {
    const identity = await loadIdentity();
    // Tek cihazlık yol oturumu LEGACY_DEVICE altında tutuyor: fan-out gerçek
    // cihaz numaralarını kullanacak, ikisi birbirine karışmasın.
    return sifreleTekCihaz(identity, peerId, LEGACY_DEVICE, plaintext,
      () => api.fetchKeyBundle(token, peerId));
  });
}

/**
 * Tek bir cihaz için zarf üret. Oturum yoksa o cihaza özel bir oturum kurulur.
 *
 * Demet TEMBEL alınıyor: oturum zaten varsa dizine hiç gidilmez. Her gönderimde
 * demet çekmek, karşı tarafın bir tek-kullanımlık prekey'ini boşuna tüketirdi.
 *
 * Kilit ÇAĞIRANDA: fan-out'ta tüm cihazlar tek sırada işleniyor, yoksa aynı
 * kişiye iki eşzamanlı gönderim cihazların yarısını birinden alırdı.
 */
async function sifreleTekCihaz(
  identity: { pub: string; sec: string },
  peerId: number,
  deviceId: number,
  plaintext: string,
  demetAl: () => Promise<PrekeyBundle>,
): Promise<SessionEnvelope> {
  let s = await loadSession(peerId, deviceId);
  // Kimlik denetimi BURADA DEĞİL (9.34 · D-634): tek cihazın anahtarına bakmak
  // yanlış alarm üretir — karşı tarafın ikinci telefonu da "daha önce
  // görmediğim anahtar"dır, oysa ortada değişiklik yoktur. Denetim yalnız
  // kişinin TAMAMI elde olduğu yerlerde yapılıyor.
  if (!s) s = await establishInitiator(identity, await demetAl());

  const message = ratchetEncrypt(s.state, plaintext, s.ad);
  await saveSession(peerId, deviceId, s);

  const benimCihaz = Number(await getSecret(K_DEVICE_ID)) || LEGACY_DEVICE;
  // Başlatan, karşıdan yanıt gelene dek öncülü ekler.
  if (s.role === 'initiator' && !s.peerReplied && s.prelude) {
    return { type: 'prekey', prelude: s.prelude, message, from: benimCihaz };
  }
  return { type: 'normal', message, from: benimCihaz };
}

// Alıcı olarak oturum kur: öncüldeki kimliklerle kendi prekey özellerini yükle,
// X3DH çalıştır, ratchet'i başlat. Kullanılan tek-kullanımlık prekey tüketilir.
async function establishResponder(
  prelude: Prelude,
): Promise<{ oturum: StoredSession; kullanilanPrekeyId: number | null }> {
  const identity = await loadIdentity();
  const spkSecret = await getSecret(signedPreKeySecret(prelude.signedPreKeyId));
  if (!spkSecret) throw new Error('İmzalı prekey özeli yok');
  const otpId = prelude.oneTimePreKeyId;
  const otpSecret = otpId != null ? await getSecret(oneTimePreKeySecret(otpId)) : null;

  const resp = respondSession({
    myIdentityPublic: identity.pub,
    myIdentitySecret: identity.sec,
    mySignedPreKeySecret: spkSecret,
    myOneTimePreKeySecret: otpSecret,
    peerIdentityKey: prelude.identityKey,
    peerEphemeralKey: prelude.ephemeralKey,
  });

  // Prekey BURADA silinmiyor (D-619). Zarfın gerçekten çözüldüğünü henüz
  // bilmiyoruz; uydurma bir zarf için silseydik, aynı öncülü taşıyan GERÇEK
  // mesaj bir daha asla açılamazdı. Silme işi çözme başarınca çağırana ait.
  return {
    oturum: {
      state: initResponder(resp.sharedSecret, { pub: x25519PublicKey(spkSecret), sec: spkSecret }),
      ad: resp.associatedData,
      role: 'responder' as const,
      peerReplied: true,
    },
    kullanilanPrekeyId: otpId,
  };
}

/** Sunucunun bu kuruluma ayırdığı cihaz numarası; henüz kaydolmadıysa yok. */
export async function myDeviceId(): Promise<number | undefined> {
  const raw = Number(await getSecret(K_DEVICE_ID));
  return Number.isInteger(raw) && raw > 0 ? raw : undefined;
}

/**
 * Mesajı OKUYABİLMESİ GEREKEN her cihaz için ayrı zarf üret (9.34 · D-627).
 *
 * Liste iki kümeden oluşuyor: karşı tarafın cihazları ve KENDİ diğer
 * cihazlarım. İkincisi unutulursa insan kendi ikinci telefonunda yazdığını
 * göremez — karşı tarafa adreslenmiş kopyayı açacak anahtar onda yok (D-611).
 *
 * `legacy`, eski sürümlerin okuduğu tek alana konacak zarf: dizin cihazları en
 * son kullanılan önce sıraladığı için listenin ilki tam olarak o alanın
 * hedeflediği cihazdır.
 *
 * Kendi cihazlarım AYRI kilit altında işleniyor, iç içe değil: iki kilidi üst
 * üste tutmak, ileride biri ötekini beklediğinde kilitlenme riski açardı.
 */
export async function encryptForAllDevices(
  token: string,
  myUserId: number,
  peerId: number,
  plaintext: string,
): Promise<{ envelopes: MessageEnvelope[]; legacy: string }> {
  const identity = await loadIdentity();
  // Yalnız LİSTE alınıyor; demet, oturumu OLMAYAN cihaz için tembel çekiliyor
  // (9.34 · D-630). Hepsini peşin çekmek, oturum zaten kuruluyken bile her
  // mesajda karşı tarafın her cihazından bir tek-kullanımlık prekey yakardı.
  const peer = await api.fetchDeviceList(token, peerId);
  // 9.34 / D-634 — kimlik denetimi LİSTE üzerinde: elimizde kişinin bütün
  // cihazları var. Tek tek baksaydık, baştan iki telefonu olan biri ikinci
  // cihazı yüzünden ilk temasta uyarı doğururdu.
  await identitiesSeen(peerId, peer.devices.map((d) => d.identity_key));

  const envelopes: MessageEnvelope[] = [];
  let legacy = '';
  await sirayla(peerId, async () => {
    for (const cihaz of peer.devices) {
      const zarf = await sifreleTekCihaz(
        identity, peerId, cihaz.device_id, plaintext,
        () => api.fetchDeviceKeyBundle(token, peerId, cihaz.device_id));
      const content = JSON.stringify(zarf);
      envelopes.push({ user_id: peerId, device_id: cihaz.device_id, content });
      // İlk cihaz = en son kullanılan = eski alanın hedefi.
      if (!legacy) legacy = content;
    }
  });

  // Kendi diğer cihazlarım. Buradaki bir hata MESAJI ENGELLEMEMELİ: karşı taraf
  // zaten okuyabiliyor, kaybedilen yalnız kendi ikinci cihazımdaki kopyadır.
  try {
    const benim = await api.fetchDeviceList(token, myUserId);
    const benimCihaz = Number(await getSecret(K_DEVICE_ID)) || LEGACY_DEVICE;
    const otekiler = benim.devices.filter((d) => d.device_id !== benimCihaz);
    if (otekiler.length > 0) {
      await sirayla(myUserId, async () => {
        for (const cihaz of otekiler) {
          const zarf = await sifreleTekCihaz(
            identity, myUserId, cihaz.device_id, plaintext,
            () => api.fetchDeviceKeyBundle(token, myUserId, cihaz.device_id));
          envelopes.push({
            user_id: myUserId, device_id: cihaz.device_id, content: JSON.stringify(zarf),
          });
        }
      });
    }
  } catch {
    // Tek cihazlı hesapta 404 normaldir; başka hatada da mesajı durdurmuyoruz.
  }

  return { envelopes, legacy };
}

/**
 * Gönderim kararı: karşı tarafla şifreleyebiliyorsak zarfı, yoksa düz metni ver
 * (9.34 · D-610). Şifreleme "iki taraf da anahtar taşıyorsa" açılır.
 *
 * GERİ DÜŞÜŞ KASTEN DAR: yalnız karşı tarafın dizinde demeti YOKSA (404) düz
 * metne dönülür. Her hatayı yutup düz metne düşmek, sessiz bir şifreleme
 * düşürmesi olurdu — ağ hatası ya da bir ratchet kusuru, kullanıcı fark etmeden
 * mesajı açık gönderirdi. Diğer hatalar yukarı fırlar.
 */
export async function encryptIfPossible(
  token: string,
  peerId: number,
  plaintext: string,
  myUserId?: number,
): Promise<{ content: string; type: 'encrypted' | 'text'; envelopes?: MessageEnvelope[]; reason?: PlainReason }> {
  try {
    // Kendi kimliğimizi bilmiyorsak dağıtamayız: hangi cihazların bize ait
    // olduğunu ayırt edemeyiz. O durumda tek cihazlık yol sürüyor.
    if (myUserId === undefined) {
      const envelope = await encryptMessage(token, peerId, plaintext);
      return { content: JSON.stringify(envelope), type: 'encrypted' };
    }
    const { envelopes, legacy } = await encryptForAllDevices(token, myUserId, peerId, plaintext);
    return { content: legacy, type: 'encrypted', envelopes };
  } catch (error) {
    const bundleYok = error instanceof ApiError && error.status === 404;
    // Kendi cihaz kimliğimiz henüz üretilmemişse de şifreleyemeyiz; bu da
    // kullanıcının mesajını engellememeli.
    const kimlikYok = error instanceof Error && error.message.includes('Cihaz kimliği yok');
    // 9.34 / D-661 — düz metne neden düşüldüğü sohbette söyleniyor: geri düşüş
    // artık sessiz değil.
    if (kimlikYok) return { content: plaintext, type: 'text', reason: 'self' };
    if (bundleYok) return { content: plaintext, type: 'text', reason: 'peer' };
    throw error;
  }
}

/**
 * Düz metne düşmenin iki nedeni (D-610, D-611): karşı tarafın dizinde anahtarı
 * yok (`peer`) ya da bu cihazın kimliği yok (`self`).
 */
export type PlainReason = 'peer' | 'self';

/**
 * Bu konuşmada ŞİMDİ yazılan mesaj şifreli gidecek mi (9.34 · D-661).
 *
 * Furkan'la konuşma haftalarca şifresiz gitti ve bunu gösteren hiçbir şey yoktu:
 * onun telefonundaki uygulama şifreleme gelmeden önceki sürümde kalmıştı.
 * Sohbet artık bunu söylüyor. Yanlış alarm vermemek için yalnız KESİN durumlar
 * raporlanıyor:
 * - `on`: karşı tarafın en az bir cihazı dizinde, bu cihazın kimliği var.
 * - `peer`: dizin bu kişi için 404 diyor; gönderilecek mesaj düz metin.
 * - `self`: bu ortam anahtar saklayamıyor (ör. tarayıcının gizli penceresi).
 * - `unknown`: ağ hatası ya da kimlik henüz üretilmemiş (açılışta kayıt sürüyor
 *   olabilir). Şerit gösterilmez; gönderimin sonucu durumu yine düzeltir.
 *
 * Açılışta bir liste isteği daha atıyor; liste ucu tek satırlık bir sorgu.
 */
export type EncryptionState = 'on' | PlainReason | 'unknown';

export async function conversationEncryption(token: string, peerId: number): Promise<EncryptionState> {
  if (!secureStorageAvailable()) return 'self';
  const pub = await getSecret(IDENTITY_PUBLIC);
  const sec = await getSecret(IDENTITY_SECRET);
  if (!pub || !sec) return 'unknown';
  try {
    const liste = await api.fetchDeviceList(token, peerId);
    return liste.devices.length > 0 ? 'on' : 'peer';
  } catch (error) {
    return error instanceof ApiError && error.status === 404 ? 'peer' : 'unknown';
  }
}

/** Bir kişiden gelen şifreli zarfı düz metne çevir. */
export function decryptMessage(peerId: number, envelope: SessionEnvelope): Promise<string> {
  const deviceId = gonderenCihaz(envelope);
  return sirayla(peerId, async () => {
    const mevcut = await loadSession(peerId, deviceId);
    if (mevcut) {
      try {
        const plaintext = ratchetDecrypt(mevcut.state, envelope.message, mevcut.ad);
        // Başlatan karşıdan mesaj çözdü — artık öncüle gerek yok.
        if (mevcut.role === 'initiator') mevcut.peerReplied = true;
        await saveSession(peerId, deviceId, mevcut);
        return plaintext;
      } catch (hata) {
        // Mevcut oturum açamadı. Elde bir ÖNCÜL yoksa yapacak bir şey yok.
        // Varsa, karşı taraf cihaz değiştirmiş olabilir (D-619): aşağıda
        // öncülden yeni bir oturum kurulup DENENİYOR. Buradaki başarısız deneme
        // hiçbir şey kaydetmedi, yani mevcut oturum olduğu gibi duruyor.
        if (envelope.type !== 'prekey') throw hata;
      }
    } else if (envelope.type !== 'prekey') {
      throw new Error('Oturum yok ve öncül eksik');
    }

    // Öncülden yeni oturum. ÖNCE çöz, SONRA kaydet: uydurma bir zarf var olan
    // oturumu sıfırlatamamalı, yoksa herkes bir zarf yollayıp sohbeti
    // koparabilirdi. Güvenlik numarası yüzeyimiz yok, bu yüzden "yeni kimliği
    // kabul et" kararının tek dayanağı zarfın GERÇEKTEN çözülmesi.
    if (envelope.type !== 'prekey') throw new Error('Oturum yok ve öncül eksik');
    const { oturum, kullanilanPrekeyId } = await establishResponder(envelope.prelude);
    const plaintext = ratchetDecrypt(oturum.state, envelope.message, oturum.ad);
    await saveSession(peerId, deviceId, oturum);
    // Ancak şimdi tüketilmiş sayılır.
    if (kullanilanPrekeyId != null) {
      await deleteSecret(oneTimePreKeySecret(kullanilanPrekeyId));
    }
    return plaintext;
  });
}
